/**
 * Notification handler buffering layer.
 *
 * Two-phase initialization pattern:
 *
 *   Phase 1 — `initializeNotifications()` (called in App/index.js at JS startup):
 *     Calls PushNotification.configure() with stub handlers. Each stub checks whether a real
 *     handler has been wired up yet; if not, it pushes the call into `_pendingCalls` so no
 *     event is lost before the UI is ready.
 *
 *   Phase 2 — `configureNotifications()` (called in RootContainer constructor):
 *     Registers the actual handlers (onRegister, onNotification, onRegistrationError, and
 *     onInitialNotification via PushNotification.popInitialNotification). Any calls buffered
 *     during Phase 1 are replayed immediately.
 *
 * This guarantees that push tokens and notifications received between JS evaluation and
 * RootContainer mount are not silently dropped.
 */
import { Platform } from 'react-native'
import PushNotification, {
  type PushNotificationOptions,
  type ReceivedNotification,
} from 'react-native-push-notification'

// It is possible to receive notifications before the handlers are set up,
// so we need to store them until the handlers are configured.
type HandlerKey = 'onRegister' | 'onNotification' | 'onRegistrationError'
type HandlersMap = {
  [K in HandlerKey]: NonNullable<PushNotificationOptions[K]>
}
type PendingHandlerCall<K extends HandlerKey = HandlerKey> = [K, Parameters<HandlersMap[K]>]

/**
 * Enum representing expected values for the `category` property in push notifications payloads.
 * See DIANotificationForegroundService.java and DIAPushNotificationListenerService.java.
 */
export enum NotificationCategory {
  ACCEPT_CALL = 'ACCEPT_CALL',
  FULL_SCREEN_CALL = 'FULL_SCREEN_CALL',
  VIDEO_CALL = 'VIDEO_CALL',
}

/**
 * Unified model for notification payload data. This is not exhaustive.
 */
export type NotificationData = {
  brandId?: string
  category?: NotificationCategory
  channelId?: string
  deeplink?: string
  data?: {
    pinpoint?: {
      deeplink?: string
    }
  }
  /**
   * payload samples https://academy.insiderone.com/docs/react-native-set-callback
   */
  ins_dl_internal?: string
  'pinpoint.deeplink'?: string
  /** Set for VoIP notifications. See https://github.com/dialoguemd/engage/blob/5cffc3cf8be561e93c71cbdf62291a5da3142dd4/engage/routers/channel/push_voip.py#L30-L36 */
  sessionId?: string
}

/**
 * Unified interface for notification objects as provided by the react-native-push-notification library.
 * - We cast the `data` property with our own interface, for convenience of usage.
 * - We omit the `userInfo` property since the onNotification handler receives the object without
 * that property, for example.
 */
export type TypedNotification =
  | (Omit<ReceivedNotification, 'data' | 'userInfo'> & {
      data: NotificationData
      // TODO: monitor if this is being used anywhere, if not remove it
      // There's a long history behind this and it's not well documented. See:
      // - Original PR: https://github.com/dialoguemd/member-app/pull/1904
      // - Conditionality: https://dialoguemd.atlassian.net/browse/DIA-40852 & https://github.com/dialoguemd/member-app/pull/2866
      // - Library code has a single mention, for iOS only: https://github.com/zo0r/react-native-push-notification/blob/fcf40a05175bb79fb5fdfe2ac31bb797bc5008bc/index.js#L370-L371,
      // but we never should be receiving an untransformed object, aka we should never receive an object with `getData()` without `data` being set to what we expect.
      getData?: () => NotificationData
    })
  | null

export type ConfigureNotificationsParams = HandlersMap & {
  onInitialNotification: (notification: TypedNotification) => void
}

let _pendingCalls: PendingHandlerCall[] = []
let _handlers: Partial<HandlersMap> = {}

const createHandler = <const K extends HandlerKey>(key: K) => {
  return (...args: Parameters<HandlersMap[K]>): void => {
    const handler = _handlers[key]
    if (handler) {
      // @ts-expect-error Typescript doesn't support union of parameters
      handler(...args)
    } else {
      _pendingCalls.push([key, args])
    }
  }
}

export const initializeNotifications = () => {
  PushNotification.configure({
    onRegister: createHandler('onRegister'),
    onNotification: createHandler('onNotification'),
    onRegistrationError: createHandler('onRegistrationError'),
    permissions: {
      alert: true,
      badge: true,
      sound: true,
    },
    popInitialNotification: false,
    requestPermissions: false,
  })
}

export const configureNotifications = ({
  onRegister,
  onNotification,
  onInitialNotification,
  onRegistrationError,
}: ConfigureNotificationsParams) => {
  _handlers.onRegister = onRegister
  _handlers.onNotification = onNotification
  _handlers.onRegistrationError = onRegistrationError

  PushNotification.popInitialNotification(onInitialNotification)

  _pendingCalls.forEach(([key, args]) => {
    // @ts-expect-error Typescript doesn't support union of parameters
    _handlers[key]?.(...args)
  })
  _pendingCalls = []
}

export const getDataFromNotification = (
  notification: TypedNotification
): NotificationData | undefined => {
  if (!notification) return

  const { data, getData } = notification
  return data || (typeof getData === 'function' ? getData() : undefined)
}

export const getChannelIdFromNotification = (
  notification: TypedNotification
): string | undefined => {
  const data = getDataFromNotification(notification)
  return data?.channelId
}

/**
 * Extracts a deeplink URL from a notification payload.
 *
 * Platform difference:
 *   - iOS: deeplink is only extracted when the user actively tapped the notification
 *     (`userInteraction === true`). This prevents the app from navigating when a notification
 *     is received silently in the foreground.
 *   - Android: deeplink is always extracted regardless of user interaction, because Android
 *     only calls onNotification after the user taps the notification tray item.
 *
 * Deeplink sources checked (in priority order):
 *   1. `data.deeplink`
 *   2. `data.ins_dl_internal` (Insider marketing push, flat key on both platforms)
 *   3. `data.data.pinpoint.deeplink` (iOS)  /  `data['pinpoint.deeplink']` (Android)
 */
export const getDeeplinkFromNotification = (
  notification: TypedNotification
): string | undefined => {
  if (notification) {
    const data = getDataFromNotification(notification)

    // Check to see if the notifcation was interacted with on iOS as well
    if (Platform.OS === 'ios') {
      if (notification.userInteraction) {
        if (data) {
          if (data.deeplink) {
            return data.deeplink
          }
          if (data.ins_dl_internal) {
            return data.ins_dl_internal
          }
          return data.data?.pinpoint?.deeplink
        }
      }
    } else {
      // Android
      if (data) {
        if (data.deeplink) {
          return data.deeplink
        }
        if (data.ins_dl_internal) {
          return data.ins_dl_internal
        }
        return data['pinpoint.deeplink'] as string | undefined
      }
    }
  }

  return undefined
}

/**
 * Forwards a push notification received via the brownfield bridge into the
 * standard react-native-push-notification pipeline.
 *
 * The native side calls this for every push received (not only tapped ones),
 * so `userInteraction` is NOT forced to true — the downstream iOS deeplink
 * guard (`getDeeplinkFromNotification`) will only extract a deeplink when the
 * user actually tapped the notification.
 *
 * The raw `userInfo` dict is wrapped under `data` so that
 * `getDataFromNotification()` can find `category`, `deeplink`, `sessionId`, etc.
 */
export const handlePushNotificationFromBridge = (userInfo: Record<string, unknown>) => {
  if (!userInfo) return

  const notification = {
    data: userInfo as unknown as NotificationData,
    userInteraction: (userInfo.userInteraction as boolean) ?? false,
    finish: () => {},
  }

  const handler = _handlers.onNotification
  if (handler) {
    // @ts-expect-error Bridge notification is a partial ReceivedNotification — only data, userInteraction and finish are set
    handler(notification)
  } else {
    // Buffer until configureNotifications() replays _pendingCalls
    _pendingCalls.push([
      'onNotification',
      [notification as unknown as Parameters<HandlersMap['onNotification']>[0]],
    ])
  }
}
